"use client"

import React from "react"
import { Message } from "@locmod/intl"

import { Icon, type IconName } from "components/ui"
import { Href } from "components/navigation"
import { SxMarketFilter } from "components/inputs"
import { useSxMarketFilter } from "contexts"
import messages from "./messages"
import type { SxLeague } from "@/SX/types/markets"

export const SxSportSkeleton: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => {
  return (
    <div>
      <div className="flex items-center justify-between py-3 px-4">
        <div className="flex items-center">
          <div className="bone size-6 mr-3 rounded-full" />
          <div className="bone h-6 w-20 rounded-md" />
        </div>
      </div>
      <div>{children}</div>
    </div>
  )
}

type SxSportProps = {
  sport: any
  isPage?: boolean
  withLink?: boolean
  children?: React.ReactNode
  showFilter?: boolean
  league?: SxLeague[]
}

const SxSport: React.FC<SxSportProps> = ({
  children,
  sport,
  isPage = false,
  withLink = true,
  showFilter = false,
  league,
}) => {
  const { slug, name } = sport
  const { name: leagueName } = league?.[0] || { name: "" }
  const { selectedMarkets, setSelectedMarkets } = useSxMarketFilter()

  const handleMarketFilterChange = (markets: string[]) => {
    setSelectedMarkets(markets)
  }

  return (
    <div>
      <div className="flex items-center justify-between py-3 px-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <Icon
              className="size-6 mr-3 text-brand-light-500"
              name={`sport/${slug}` as IconName}
            />
            <div className="text-caption-13 ds:text-heading-h4 font-semibold">
              {isPage && league ? (
                <Message
                  value={{
                    ...messages.sport,
                    values: { sportName: leagueName },
                  }}
                />
              ) : isPage ? (
                <Message
                  value={{ ...messages.sport, values: { sportName: name } }}
                />
              ) : (
                name
              )}
            </div>
          </div>
          {showFilter && (
            <div className="flex items-center ">
              <span className="text-accent-grey-400 text-caption-13 ds:text-caption-14 font-medium">
                Markets:
              </span>
              <SxMarketFilter
                selectedValues={selectedMarkets}
                onMultiFilterChange={handleMarketFilterChange}
                placeholder="Select markets"
                size="sm"
              />
            </div>
          )}
        </div>
        {Boolean(withLink && !isPage) && (
          <Href
            to={`/${slug}`}
            className="bg-accent-grey-1000 rounded-full text-accent-grey-200 hover:text-accent-grey-30 border border-accent-grey-850 size-6 flex items-center justify-center"
          >
            <Icon className="size-4" name="interface/chevron_right" />
          </Href>
        )}
      </div>
      <div>
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(
              child as React.ReactElement<{ showFilter?: boolean }>,
              { showFilter }
            )
          }
          return child
        })}
      </div>
    </div>
  )
}

export default SxSport
